import React from 'react';

import { SecondaryButton } from '@components/Button/variants';
import Byline from '@components/Byline';
import VerticalAdvertisement from '@components/FeaturedPartnerAd/Vertical';
import ArticleFeedItem from '@components/FeedItems/ArticleFeedItem';
import ArticleHeader from '@components/FeedItems/ArticleHeader';
import ArticleImage from '@components/FeedItems/ArticleImage';
import Highlight from '@components/FeedItems/Highlight';
import UnitHeader from '@components/FeedItems/UnitHeader';
import useEventTracking from '@hooks/useEventTracking';
import { Advertisement, Article, FeedUnit } from '@interfaces/homepage';
import { ClickTarget, click, transformUnitNameToLocation } from '@tracking';
import { FeedContentType } from '@utils/enums';

interface NewsletterUnitProps {
  feedUnit: FeedUnit;
  isTheWeekend?: boolean;
}

const NewsletterUnit = ({
  feedUnit: { unitName, unitDescription, viewAllUrl, content },
  isTheWeekend = false,
}: NewsletterUnitProps) => {
  const trackingLocation = transformUnitNameToLocation(unitName);
  const { Track, trackEvent } = useEventTracking({
    location: trackingLocation,
  });

  return (
    <Track>
      <div
        className="border-0 border-b border-solid border-grayscale-gray px-16 py-32 sm:px-0 sm:py-48"
        data-testid="newsletter-unit"
      >
        <UnitHeader
          isTheWeekend={isTheWeekend}
          unitName={unitName}
          unitDescription={unitDescription}
          viewAllUrl={viewAllUrl}
        />
        <div className="flex flex-col justify-between sm:flex-row">
          {content.map((contentItem, index) => {
            if (contentItem.contentType === FeedContentType.Advertisement) {
              const { sponsorName } = contentItem as Advertisement;
              return (
                <VerticalAdvertisement
                  key={sponsorName}
                  advertisement={contentItem}
                  className="sm:max-w-[258px]"
                  trackingLocation={trackingLocation}
                />
              );
            } else {
              const {
                authors,
                commentsCount,
                highlight,
                id,
                isPro,
                picture,
                publishedAt,
                slug,
                title,
                skipPictureOptimization,
              } = contentItem as Article;
              return (
                <ArticleFeedItem
                  articleId={id}
                  articleAuthors={authors}
                  contentIndex={index}
                  key={`${unitName}-${id}`}
                  className="flex flex-col pb-36 sm:w-min sm:pb-0"
                >
                  <ArticleImage
                    className="sm:h-[calc(330px*9/16)] sm:w-[330px] lg:h-[calc(416px*9/16)] lg:w-[416px]"
                    id={id}
                    isPro={isPro}
                    picture={picture}
                    skipOptimization={skipPictureOptimization}
                    sizes="(min-width: 1200px) 500px, (min-width: 768px) 300px, (min-width: 480px) 600px, 400px"
                    slug={slug}
                    title={title}
                  />
                  <div className="mt-8 sm:mt-12">
                    {highlight && <Highlight highlight={highlight} />}
                    <ArticleHeader
                      className="latest-article-title text-headline-xs sm:text-headline-xxs"
                      title={title}
                      slug={slug}
                    />
                    <Byline
                      authors={authors}
                      commentsCount={commentsCount}
                      publishedAt={publishedAt}
                      slug={slug}
                    />
                  </div>
                </ArticleFeedItem>
              );
            }
          })}
        </div>
        <SecondaryButton
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ViewAllArticles }))}
          as="link"
          href={viewAllUrl}
          className="inline-block sm:hidden"
        >
          View all
        </SecondaryButton>
      </div>
    </Track>
  );
};

export default NewsletterUnit;
